<template>
  <div v-if="project.created_dt" class="project-form">
    <form>
      <section>
        <h1>{{ $t('projects.details.labels.projectDetails') }}</h1>
        <div class="form-group">
          <div class="form-control">
            <label>
              {{ $t('projects.details.labels.clientName') }}
              <span class="asterisk">*</span>
            </label>
            <InputField
              v-if="updatedProject.client_name"
              v-model="updatedProject.client_name"
              type="text"
              class="bordered"
              :class="{ error: formErrors['clientName'] }"
              name="project-client"
              picker="minus-circle"
              readonly
              @picker-click="clearClient"
            />
            <tol-type-ahead
              v-else
              :search-params="{
                url: '/assignments/user-quick-search',
                searchKeys: ['name'],
                objectType: 'client'
              }"
              class="bordered"
              :menu-label="$t('projects.details.labels.searchClients')"
              @select-item="updateOnScreenValue('client', $event)"
            >
              <template v-slot="{ value }">
                {{ value.name }}
              </template>
            </tol-type-ahead>
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.details.labels.endClientName') }}
              <span class="asterisk">*</span>
            </label>
            <InputField
              v-if="updatedProject.end_client_name"
              v-model="updatedProject.end_client_name"
              type="text"
              class="bordered"
              :class="{ error: formErrors['endClientName'] }"
              name="project-end-client"
              picker="minus-circle"
              readonly
              @picker-click="clearEndClient"
            />
            <tol-type-ahead
              v-else
              :search-params="{
                url: '/assignments/user-quick-search',
                searchKeys: ['name'],
                objectType: 'client'
              }"
              class="bordered"
              :menu-label="$t('projects.details.labels.searchEndClients')"
              @select-item="updateOnScreenValue('endClient', $event)"
            >
              <template v-slot="{ value }">
                {{ value.name }}
              </template>
            </tol-type-ahead>
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.details.labels.projectName') }}
              <span class="asterisk">*</span>
            </label>
            <InputField
              v-model="updatedProject.name"
              type="text"
              class="bordered"
              :class="{ error: formErrors['projectName'] }"
              @input="updateOnScreenValue('name', $event)"
            />
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.details.labels.startDate') }}
              <span class="asterisk">*</span>
            </label>
            <form-date-picker
              v-model="projectStartDate"
              :type="formErrors['start_date'] ? 'bordered error' : 'bordered'"
              @change="updateOnScreenValue('startDate', $event)"
            >
            </form-date-picker>
          </div>
          <div class="form-control">
            <label>{{ $t('projects.details.labels.endDate') }}</label>
            <form-date-picker
              :value="projectEndDate"
              type="bordered"
              @change="updateOnScreenValue('endDate', $event)"
            >
            </form-date-picker>
          </div>
          <div class="form-control">
            <label>
              {{ $t('projects.details.labels.status') }}
              <span class="asterisk">*</span>
            </label>
            <dropdown-select
              :menu-items="statuses"
              :selected-item="updatedProject.status"
              class="bordered"
              @select-item="updateOnScreenValue('status', $event)"
            >
            </dropdown-select>
          </div>
        </div>
      </section>
      <div class="module-action-container">
        <TolButton
          v-if="canUserSave"
          button-type="save"
          :label="$t('projects.details.labels.save')"
          :is-loading="isLoading"
          :class="[
            'module-action',
            { disabled: !isOnScreenDataModified && project.reference_number }
          ]"
          @click="saveProjectUpdates"
        />
      </div>
    </form>
  </div>
</template>

<script>
import format from 'date-fns/format';
import { dateUtils } from '@/mixins/date-utils';
import DropdownSelect from '@/app/components/DropdownSelect';
import FormDatePicker from '@/app/components/FormDatePicker';
import InputField from '@/app/components/InputField';
import { ProjectDetailsActions } from '../project-details.keys';
import TolButton from '@/app/components/TolButton';
import TolTypeAhead from '@/app/components/TolTypeAhead';

export default {
  name: 'ProjectDetailsForm',
  components: {
    DropdownSelect,
    InputField,
    FormDatePicker,
    TolButton,
    TolTypeAhead
  },
  mixins: [dateUtils],
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      updatedProject: {
        client_name: '',
        client_reference_number: null,
        end_client_name: '',
        end_client_reference_number: null,
        name: '',
        start_date: null,
        end_date: null,
        status: null
      },
      isOnScreenDataModified: false,
      statuses: ['ACTIVE', 'INACTIVE', 'RECONCILED', 'COMPLETED']
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    canUserSave() {
      if (this.currentUser) {
        return (
          this.currentUser.permissions['PROJECT'] && this.currentUser.permissions['PROJECT']['SAVE']
        );
      }
      return false;
    },
    isLoading() {
      return this.$store.getters.IsSavingProject;
    },
    formErrors() {
      return this.$store.getters.ProjectValidationErrors;
    },
    projectStartDate() {
      return this.updatedProject.start_date
        ? this.dateFromString(this.updatedProject.start_date)
        : null;
    },
    projectEndDate() {
      return this.updatedProject.end_date
        ? this.dateFromString(this.updatedProject.end_date)
        : null;
    }
  },
  watch: {
    project: {
      immediate: true,
      handler() {
        Object.keys(this.updatedProject).forEach(key => {
          this.updatedProject[key] = this.project[key];
        });
      }
    }
  },
  methods: {
    clearClient() {
      this.updatedProject.client_reference_number = null;
      this.updatedProject.client_name = '';
      this.isOnScreenDataModified = true;
    },
    clearEndClient() {
      this.updatedProject.end_client_reference_number = null;
      this.updatedProject.end_client_name = '';
      this.isOnScreenDataModified = true;
    },
    updateOnScreenValue(fieldName, event = null) {
      switch (fieldName) {
        case 'client':
          this.updatedProject.client_reference_number = event.reference_number;
          this.updatedProject.client_name = event.name;
          break;
        case 'endClient':
          this.updatedProject.end_client_reference_number = event.reference_number;
          this.updatedProject.end_client_name = event.name;
          break;
        case 'name':
          this.updatedProject.name = event;
          break;
        case 'startDate':
          this.updatedProject.start_date = format(event, 'YYYY-MM-DD');
          break;
        case 'endDate':
          this.updatedProject.end_date = format(event, 'YYYY-MM-DD');
          break;
        case 'status':
          this.updatedProject.status = event;
          break;
        default:
          break;
      }
      this.isOnScreenDataModified = true;
    },
    saveProjectUpdates() {
      if (!this.isOnScreenDataModified && this.project.reference_number) {
        return;
      }

      const updates = {};
      Object.keys(this.updatedProject).forEach(key => {
        if (
          key !== 'client_name' &&
          key !== 'end_client_name' &&
          this.updatedProject[key] !== this.project[key]
        ) {
          updates[key] = this.updatedProject[key];
        }
      });

      this.$store.dispatch(ProjectDetailsActions.SaveProject, {
        updates,
        referenceNumber: this.project.reference_number
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';

.asterisk {
  color: red;
}

.project-form {
  form {
    width: 100%;

    section {
      padding: 0 2rem;
      margin-bottom: 20px;
      background-color: $background-level-3;

      h1 {
        padding-top: 15px;
        border-bottom: 2px solid $blue;
      }

      h1,
      h2 {
        flex: 0 100%;
      }

      .form-group {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        &.right {
          justify-content: flex-end;
        }
        @media only screen and (min-width: 600px) {
          flex-direction: row;
        }
      }

      .form-control {
        padding: 10px 0;
        &.wide {
          width: 50rem;
          &.error {
            background-color: $modified-form;
          }
        }
        @media only screen and (min-width: 600px) {
          width: 12rem;
        }

        > label {
          display: block;
          font-weight: bold;
        }
      }
    }

    .bordered {
      border: 1px solid $blue;
      font-size: 1em;
      &:focus {
        outline: none;
      }
      &.small {
        width: auto;
        display: inline-flex;
        margin-right: 15px;
      }
    }

    .form-input {
      border-bottom: 1px solid $blue;
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }
      &.error {
        border-bottom: 1px solid $red;
      }
      &.small {
        width: auto;
        display: inline-flex;
        margin-right: 15px;
      }
    }
  }
  &.modified {
    border-left: 5px solid $orange;
  }

  .module-action-container {
    justify-content: flex-end;
  }

  .error {
    color: $red;
    background-color: $modified-form;
  }
}
</style>
